import moment from 'moment'
import React from 'react'
import { TidewaysContext, isProfilingSpace } from '../app/context'
import Navigator from '../app/navigator'
import Range from '../app/timeRange'
import { Application } from '../domain/Application'
import { DashboardApplicationReport } from '../domain/DashboardApplicationReport'
import { DashboardIncidents } from '../domain/DashboardIncidents'
import { Organization } from '../domain/Organization'
import BoxBody from '../helper/boxBody'
import LargeNumber from '../helper/large-number'
import Link from '../helper/link'
import Milliseconds from '../helper/milliseconds'
import Percent from '../helper/percent'
import { rangeSelectorPerformanceView } from '../history/rangeSelectorPerformanceView'
import MomentDate from '../momentDate'
import HistoryRangeSelector from './../monitor/graph/history_range'
import SlimBox from './../monitor/history/slimBox'
import { SERVICE_ALL } from '../domain/Service'

interface Props {
    navigator: Navigator
    application: Application
    organization: Organization
    data: DashboardApplicationReport
    incidents?: DashboardIncidents
}

class DashboardApplication extends React.Component<Props> {
    public render() {
        const { navigator: router, application, data, incidents } = this.props

        const params = {
            organization: this.props.organization.name,
            application: application.name,
        }

        const range = Range.fromParameters({})
        const showSlimHistory = data.date !== 'yesterday' && application.license.history && data.history

        const tidewaysContext = new TidewaysContext(params)
        const selector = rangeSelectorPerformanceView(tidewaysContext, this.props.navigator)

        if (!data) {
            return null
        }

        return (
            <BoxBody {...this.props} className={'application success'}>
                <div className="row">
                    <div className="col-md-3 col-sm-12 col-xs-12">
                        <span>
                            <span className={'fa fa-circle text-success'} />
                            &nbsp;
                        </span>
                        <a href={router.path('XhprofOrganizationBundle.Application.show', params)}>
                            <big>{application.name}</big>
                        </a>
                        {!incidents || !incidents.resolved ? null : (
                            <a
                                href={router.path('XhprofErrorBundle.Issues.incident', {
                                    ...params,
                                    page: 1,
                                    status: 2,
                                    s: SERVICE_ALL,
                                })}
                            >
                                &nbsp;
                                <span className="label label-warning" title={`${incidents.resolved} resolved incidents`}>
                                    {incidents.resolved}
                                </span>
                            </a>
                        )}
                        {!incidents || !incidents.ongoing ? null : (
                            <a
                                href={router.path('XhprofErrorBundle.Issues.incident', {
                                    ...params,
                                    page: 1,
                                    s: SERVICE_ALL,
                                })}
                            >
                                &nbsp;
                                <span className="label label-danger" title={`${incidents.ongoing} ongoing incidents`}>
                                    {incidents.ongoing}
                                </span>
                            </a>
                        )}
                    </div>
                    {!isProfilingSpace(application) ? (
                        <div className="col-md-4 col-sm-12 col-xs-12">
                            <div className="row" style={showSlimHistory ? { marginBottom: 20 } : undefined}>
                                <div className="col-md-3 text-right">
                                    {data.date ? (
                                        <big>
                                            {data.date === 'yesterday' ? (
                                                'Yesterday'
                                            ) : (
                                                <span
                                                    title={
                                                        'Current performance between ' +
                                                        moment
                                                            .utc(data.date)
                                                            .local()
                                                            .format('HH:mm') +
                                                        ' and ' +
                                                        moment
                                                            .utc(data.date)
                                                            .add(15, 'm')
                                                            .local()
                                                            .format('HH:mm')
                                                    }
                                                >
                                                    15mins
                                                </span>
                                            )}
                                        </big>
                                    ) : null}
                                </div>
                                <div className="col-md-3 text-center">
                                    <big title="Response Time">
                                        <Milliseconds value={data.responseTime * 1000} class="response-time" />
                                        &nbsp;
                                    </big>
                                </div>
                                <div className="col-md-3 text-center">
                                    <big title="Request per minute">
                                        <LargeNumber value={data.requests || 0} /> rpm&nbsp;
                                    </big>
                                </div>
                                <div className="col-md-3 text-center">
                                    <span>
                                        <big title="Failure Rate">
                                            <Percent value={data.errorRate / 100} class="error" />
                                            &nbsp;
                                        </big>
                                    </span>
                                </div>
                            </div>
                            {!showSlimHistory
                                ? null
                                : Object.entries(data.slimHistory.reports).map(([granularity, reportData]) => (
                                      <SlimBox
                                          key={application.name + granularity}
                                          data={reportData}
                                          navigator={this.props.navigator}
                                          organization={this.props.organization}
                                          application={this.props.application}
                                      />
                                  ))}
                        </div>
                    ) : null}
                    {data.history && !isProfilingSpace(application) ? (
                        <div className="col-md-5">
                            <div className="monitor">
                                <HistoryRangeSelector
                                    selector={selector}
                                    history={this.props.data.history}
                                    renderScrollbar={false}
                                    daysOnScale={1}
                                    rangeSelected={(event, dataSet, nextDataSet) => {
                                        event.preventDefault()
                                        event.stopPropagation()

                                        tidewaysContext.setRange(Range.fromStartAndEnd(dataSet.date, nextDataSet.date).limitRange())

                                        // Not using the navigator or history here
                                        // because we need a context-re-initialisation
                                        // when entering the application context.
                                        //
                                        // Once the dashboard and context
                                        // re-initialisation is fixed in this regard we
                                        // should use navigator.push(…) here.
                                        const parameters = { ...tidewaysContext.toParameters(), ...params }

                                        window.location.href = router.path('XhprofOrganizationBundle.Application.show', parameters)
                                    }}
                                />
                            </div>
                        </div>
                    ) : isProfilingSpace(application) ? (
                        <div className="col-md-8">
                            {data.recentTraces.length > 0 ? (
                                data.recentTraces.map(trace => (
                                    <div className="row" key={trace.id}>
                                        <div className="col-md-8">
                                            <Link
                                                skipHistoryApi
                                                className="profile-list-item"
                                                route="XhprofProfilerBundle.Profile.quick"
                                                params={{
                                                    profile: trace.id,
                                                    organization: data.organization,
                                                    application: data.application,
                                                    s: trace.service,
                                                    env: trace.environment,
                                                    measured: trace.time,
                                                }}
                                            >
                                                {trace.op}
                                            </Link>
                                        </div>
                                        <div className="col-md-2">
                                            <Milliseconds value={trace.wt}></Milliseconds>
                                        </div>
                                        <div className="col-md-2">
                                            <MomentDate date={moment.unix(trace.time).utc()}></MomentDate>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div>
                                    No traces are currently stored in this profiling space.{' '}
                                    {!application.recievedData ? (
                                        <Link
                                            skipHistoryApi
                                            route="XhprofOrganizationBundle.Application.installation"
                                            params={{
                                                organization: this.props.organization.name,
                                                application: application.name,
                                            }}
                                        >
                                            Setup Tideways
                                        </Link>
                                    ) : (
                                        <Link
                                            skipHistoryApi
                                            route="XhprofOrganizationBundle.Application.trigger"
                                            params={{
                                                organization: this.props.organization.name,
                                                application: application.name,
                                            }}
                                        >
                                            Trigger Trace
                                        </Link>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="col-md-5 text-center">There is no realtime chart data available for this application.</div>
                    )
                    // console.log(application)
                    // <div className="col-md-5 text-center">There is no realtime chart data available for this application.</div>
                    }
                </div>
            </BoxBody>
        )
    }
}

export default DashboardApplication
